<template>
  <v-row no-gutters>
    <v-col cols="12">
      <div :class="{ 'extra-margin': !displayAddButton }">
        <div v-for="(person, index) in people" :key="index">
          <div class="list-item">
            <v-btn
              :ripple="false"
              block
              color="white"
              class="list-item-btn"
              @click.stop="showEditDialog(person)"
            >
              <div class="list-item-text">
                <span class="list-item-name">{{ person.fullName }}</span>
                <span v-show="person.kinship" class="list-item-separator"
                  >-</span
                >
                <span class="list-item-more-info">{{
                  formatPersonKinship(person.kinship)
                }}</span>
                <BeneficiaryDonationDetails
                  v-if="relation == 'HEIR'"
                  :beneficiary="person"
                />
              </div>
            </v-btn>
          </div>
        </div>
        <CompanyTrusteeListItem
          v-if="showCompanyTrusteeListItem"
          :testator="testator"
        />
      </div>
      <v-row v-if="displayAddButton" class="add-trustee-button-container">
        <v-col cols="12" :md="showCompanyTrusteeBtn ? '6' : '12'" class="py-0">
          <div class="mt-5 mb-5">
            <AlternativeButton
              :title="`Add ${capitalize(relationName)}`"
              @gradientButtonClicked="showDialog = true"
            />
          </div>
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <AddCompanyAsTrusteeBtn
            v-if="showCompanyTrusteeBtn"
            :testator="testator"
          />
        </v-col>
      </v-row>
    </v-col>
    <PersonDialog
      v-model="showDialog"
      :person-to-update="personToUpdate"
      :people-list-for-dialog="peopleListForDialog"
      :relation="relation"
      :display-existing-people="displayExistingPeople"
      @existingPersonSelectedWithMissingData="
        existingPersonSelectedWithMissingData
      "
      @modalClosed="modalClosed"
      @personCreated="personCreated"
      :decisionMaker="decisionMaker"
      @decisionMakerRemoved="decisionMakerRemoved"
      :addPersonEmailRequired="addPersonEmailRequired"
      :emergencyContacts="emergencyContacts"
    />
  </v-row>
</template>

<script>
  import PersonDialog from './PersonDialog.vue';
  import BeneficiaryDonationDetails from './BeneficiaryDonationDetails';
  import { formatKinship } from '../../constants/js/people.js';
  import AddCompanyAsTrusteeBtn from './AddCompanyAsTrusteeBtn.vue';
  import CompanyTrusteeListItem from './CompanyTrusteeListItem.vue';

  export default {
    name: 'PeopleList',
    components: {
      PersonDialog,
      BeneficiaryDonationDetails,
      AddCompanyAsTrusteeBtn,
      CompanyTrusteeListItem,
    },
    props: {
      testator: { type: Object, required: true },
      people: { type: Array, required: true },
      relation: { type: String, required: true },
      displayExistingPeople: { type: Boolean, default: true },
      peopleForDialog: { type: Array },
      limit: { type: Number },
      decisionMaker: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      peopleDialogShow: {
        required: false,
        default: false,
        type: Boolean,
      },
      emergencyContacts: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      addPersonEmailRequired: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
    },
    data() {
      return {
        showDialog: false,
        personToUpdate: {
          fullName: null,
          dateOfBirth: null,
          kinship: null,
        },
      };
    },
    computed: {
      displayAddButton() {
        if (!this.limit) {
          return true;
        } else if (this.isTrusteeRelation) {
          const companyTrusts = this.testator.companyIsTrustee ? 1 : 0;
          return this.limit > this.people.length + companyTrusts;
        } else {
          return this.limit > this.people.length;
        }
      },
      relationName() {
        switch (this.relation) {
          case 'INSURANCE_NOMINATED_BENEFICIARY':
            return 'nominated beneficiary';
          case 'LIVING_WILL_DECISION_MAKER':
            return 'person';
          case 'WILL_LOCATION_CONTACT':
            return 'contact';
          case 'CO_EXECUTOR':
            return 'co-executor';
          case 'EMERGENCY_CONTACT':
            return 'person';
          default:
            return this.relation.toLowerCase().replace(/_/g, ' ');
        }
      },
      peopleListForDialog() {
        return this.peopleForDialog || this.peopleExcludingAlreadySelected;
      },
      peopleExcludingAlreadySelected() {
        return this.testator.people.map(p => {
          return this.people.includes(p)
            ? { ...p, disabledMessage: this.disabledMessage }
            : p;
        });
      },
      disabledMessage() {
        if (this.relationName.match('^[aieouhAIEOUH].*')) {
          return 'This person is already an ' + this.relationName;
        } else {
          return 'This person is already a ' + this.relationName;
        }
      },
      isTrusteeRelation() {
        return this.relation == 'TRUSTEE';
      },
      showCompanyTrusteeBtn() {
        return (
          this.isTrusteeRelation &&
          this.testator.company &&
          this.testator.company.offerTrustAdministration
        );
      },
      showCompanyTrusteeListItem() {
        return this.isTrusteeRelation && this.testator.companyIsTrustee;
      },
    },
    methods: {
      modalClosed() {
        this.personToUpdate = {
          fullName: null,
          dateOfBirth: null,
          kinship: null,
        };
      },
      personCreated() {
        this.$emit('personCreated');
      },
      existingPersonSelectedWithMissingData(person) {
        this.personToUpdate = person;
      },
      showEditDialog(person) {
        this.personToUpdate = person;
        this.showDialog = true;
      },
      formatPersonKinship(kinship) {
        return formatKinship(kinship, this.testator);
      },
      decisionMakerRemoved() {
        this.$emit('decisionMakerRemoved');
      },
      capitalize(relationName) {
        return (
          relationName.charAt(0).toUpperCase() +
          relationName.slice(1).toLowerCase()
        );
      },
    },
    watch: {
      peopleDialogShow(newVal) {
        this.showDialog = newVal;
      },
    },
  };
</script>

<style scoped lang="scss">
  .extra-margin {
    margin-bottom: 25px;
  }

  .add-trustee-button-container {
    display: flex;
    flex-direction: column;
  }
  .add-item-btn {
    margin-bottom: 12px !important;
  }
</style>
